import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import type {
  ApprovalWorkflowDetail,
  ApprovalStageResult,
  ApprovalAction,
} from '@/lib/api/types'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAvatarColor } from '@/lib/avatar-colors'

interface ApprovalTimelineProps {
  workflow: ApprovalWorkflowDetail
  stageResults: ApprovalStageResult[]
  currentStageId: string
  className?: string
}

export function ApprovalTimeline({
  workflow,
  stageResults,
  currentStageId,
  className,
}: ApprovalTimelineProps) {
  const sortedStages = [...workflow.stages].sort((a, b) => a.order - b.order)

  const getStageStatus = (stageId: string) => {
    const result = stageResults.find((r) => r.stageId === stageId)
    if (result) {
      return result.action
    }
    if (stageId === currentStageId) {
      return 'Current'
    }
    // Check if stage comes before or after current stage
    const stage = sortedStages.find((s) => s.id === stageId)
    const currentStage = sortedStages.find((s) => s.id === currentStageId)
    if (stage && currentStage && stage.order < currentStage.order) {
      return 'Skipped' // Should have a result but doesn't
    }
    return 'Pending'
  }

  const getStageResult = (stageId: string) => {
    return stageResults.find((r) => r.stageId === stageId)
  }

  const getStatusIcon = (status: ApprovalAction | 'Current' | 'Pending' | 'Skipped') => {
    switch (status) {
      case 'Approve':
        return <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
      case 'Reject':
        return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
      case 'RequestChanges':
        return <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
      case 'Current':
        return (
          <div className="h-5 w-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
          </div>
        )
      case 'Pending':
      case 'Skipped':
      default:
        return <Clock className="h-5 w-5 text-neutral-400" />
    }
  }

  const getStatusColor = (status: ApprovalAction | 'Current' | 'Pending' | 'Skipped') => {
    switch (status) {
      case 'Approve':
        return 'bg-green-100 dark:bg-green-900/30'
      case 'Reject':
        return 'bg-red-100 dark:bg-red-900/30'
      case 'RequestChanges':
        return 'bg-orange-100 dark:bg-orange-900/30'
      case 'Current':
        return 'bg-blue-100 dark:bg-blue-900/30'
      case 'Pending':
      case 'Skipped':
      default:
        return 'bg-neutral-100 dark:bg-neutral-800'
    }
  }

  const getStatusLabel = (status: ApprovalAction | 'Current' | 'Pending' | 'Skipped') => {
    switch (status) {
      case 'Approve':
        return 'Approved'
      case 'Reject':
        return 'Rejected'
      case 'RequestChanges':
        return 'Changes Requested'
      case 'Current':
        return 'In Review'
      case 'Pending':
        return 'Pending'
      case 'Skipped':
        return 'Skipped'
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return (
    <div className={cn('space-y-1', className)}>
      {sortedStages.map((stage, index) => {
        const status = getStageStatus(stage.id)
        const result = getStageResult(stage.id)
        const isLast = index === sortedStages.length - 1

        return (
          <div key={stage.id} className="relative">
            {/* Connector Line */}
            {!isLast && (
              <div
                className={cn(
                  'absolute left-5 top-12 w-0.5 h-full -translate-x-1/2',
                  status === 'Pending' || status === 'Skipped'
                    ? 'bg-neutral-200 dark:bg-neutral-700'
                    : status === 'Approve'
                    ? 'bg-green-300 dark:bg-green-700'
                    : status === 'Reject'
                    ? 'bg-red-300 dark:bg-red-700'
                    : status === 'RequestChanges'
                    ? 'bg-orange-300 dark:bg-orange-700'
                    : 'bg-blue-300 dark:bg-blue-700'
                )}
              />
            )}

            {/* Stage Item */}
            <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
              {/* Status Icon */}
              <div
                className={cn(
                  'p-2 rounded-full shrink-0',
                  getStatusColor(status)
                )}
              >
                {getStatusIcon(status)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {stage.name}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      status === 'Current' && 'border-blue-500 text-blue-600 dark:text-blue-400',
                      status === 'Approve' && 'border-green-500 text-green-600 dark:text-green-400',
                      status === 'Reject' && 'border-red-500 text-red-600 dark:text-red-400',
                      status === 'RequestChanges' &&
                        'border-orange-500 text-orange-600 dark:text-orange-400'
                    )}
                  >
                    {getStatusLabel(status)}
                  </Badge>
                </div>

                {stage.description && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {stage.description}
                  </p>
                )}

                {/* Result Details */}
                {result && (
                  <div className="flex items-center gap-3 mt-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className={`text-xs ${getAvatarColor(result.actionBy || result.actionByName).bg} ${getAvatarColor(result.actionBy || result.actionByName).text}`}>
                        {getInitials(result.actionByName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
                      <span>{result.actionByName}</span>
                      <ChevronRight className="h-3 w-3" />
                      <span>{formatDate(result.actionAt)}</span>
                    </div>
                  </div>
                )}

                {/* Result Comments */}
                {result?.comments && (
                  <div className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded text-sm text-neutral-600 dark:text-neutral-300">
                    "{result.comments}"
                  </div>
                )}

                {/* Current Stage Approvers */}
                {status === 'Current' && stage.approvers.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                      Awaiting approval from:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {stage.approvers.map((approver) => (
                        <Badge key={approver.id} variant="secondary" className="text-xs">
                          {approver.approverName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
